import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

const FlightViewContext = createContext();

export const useFlightView = () => {
  const context = useContext(FlightViewContext);
  if (!context) {
    throw new Error('useFlightView must be used within a FlightViewProvider');
  }
  return context;
};

export const FlightViewProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { hasActiveSubscription, subscription, loading } = useSubscription();
  const [flightViews, setFlightViews] = useState(0);
  const [hasViewedFirstFlight, setHasViewedFirstFlight] = useState(false);
  const [lastViewDate, setLastViewDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to get today's date string
  const getTodayString = () => {
    return new Date().toDateString();
  };

  // Helper function to get today's date in YYYY-MM-DD format for Firebase
  const getTodayFirebaseString = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Load flight views from Firebase (for signed-in users) or localStorage (for anonymous users)
  useEffect(() => {
    const loadFlightViews = async () => {
      setIsLoading(true);
      
      if (currentUser) {
        // Signed-in user: Load from Firebase
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const todayFirebase = getTodayFirebaseString();
            
            if (userData.flightViews) {
              const lastViewDateFirebase = userData.flightViews.lastViewDate;
              
              // Check if it's a new day
              if (lastViewDateFirebase !== todayFirebase) {
                // New day - reset views in Firebase
                console.log('🔄 New day detected for signed-in user - resetting views in Firebase');
                await updateDoc(userDocRef, {
                  'flightViews.flightCount': 0,
                  'flightViews.lastViewDate': todayFirebase,
                  'flightViews.hasViewedFirstFlight': false,
                  'flightViews.updatedAt': serverTimestamp()
                });
                
                setFlightViews(0);
                setHasViewedFirstFlight(false);
                setLastViewDate(todayFirebase);
              } else {
                // Same day - load from Firebase
                setFlightViews(userData.flightViews.flightCount || 0);
                setHasViewedFirstFlight(userData.flightViews.hasViewedFirstFlight || false);
                setLastViewDate(userData.flightViews.lastViewDate || todayFirebase);
              }
            } else {
              // First time user - initialize in Firebase
              console.log('🆕 First time signed-in user - initializing in Firebase');
              await setDoc(userDocRef, {
                flightViews: {
                  flightCount: 0,
                  lastViewDate: todayFirebase,
                  hasViewedFirstFlight: false,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp()
                }
              }, { merge: true });
              
              setFlightViews(0);
              setHasViewedFirstFlight(false);
              setLastViewDate(todayFirebase);
            }
          } else {
            // User document doesn't exist - create it
            console.log('🆕 Creating new user document in Firebase');
            const todayFirebase = getTodayFirebaseString();
            await setDoc(userDocRef, {
              flightViews: {
                flightCount: 0,
                lastViewDate: todayFirebase,
                hasViewedFirstFlight: false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              }
            });
            
            setFlightViews(0);
            setHasViewedFirstFlight(false);
            setLastViewDate(todayFirebase);
          }
        } catch (error) {
          console.error('❌ Error loading flight views from Firebase:', error);
          // Fallback to localStorage for signed-in users if Firebase fails
          const userKey = currentUser.uid;
          const today = getTodayString();
          const savedViews = localStorage.getItem(`flightViews_${userKey}`);
          const savedFirstView = localStorage.getItem(`hasViewedFirstFlight_${userKey}`);
          const savedLastViewDate = localStorage.getItem(`lastViewDate_${userKey}`);
          
          if (savedLastViewDate && savedLastViewDate !== today) {
            setFlightViews(0);
            setHasViewedFirstFlight(false);
            setLastViewDate(today);
            localStorage.setItem(`flightViews_${userKey}`, '0');
            localStorage.setItem(`hasViewedFirstFlight_${userKey}`, 'false');
            localStorage.setItem(`lastViewDate_${userKey}`, today);
          } else {
            setFlightViews(savedViews ? parseInt(savedViews, 10) : 0);
            setHasViewedFirstFlight(savedFirstView === 'true');
            setLastViewDate(savedLastViewDate || today);
          }
        }
      } else {
        // Anonymous user: Load from localStorage
        const userKey = 'anonymous_user';
        const today = getTodayString();
        
        const savedViews = localStorage.getItem(`flightViews_${userKey}`);
        const savedFirstView = localStorage.getItem(`hasViewedFirstFlight_${userKey}`);
        const savedLastViewDate = localStorage.getItem(`lastViewDate_${userKey}`);
        
        // Check if it's a new day - reset views if so
        if (savedLastViewDate && savedLastViewDate !== today) {
          console.log('🔄 New day detected for anonymous user - resetting views');
          setFlightViews(0);
          setHasViewedFirstFlight(false);
          setLastViewDate(today);
          localStorage.setItem(`flightViews_${userKey}`, '0');
          localStorage.setItem(`hasViewedFirstFlight_${userKey}`, 'false');
          localStorage.setItem(`lastViewDate_${userKey}`, today);
        } else {
          // Same day - load saved data
          setFlightViews(savedViews ? parseInt(savedViews, 10) : 0);
          setHasViewedFirstFlight(savedFirstView === 'true');
          setLastViewDate(savedLastViewDate || today);
        }
      }
      
      setIsLoading(false);
    };

    loadFlightViews();
  }, [currentUser]);

  // Additional effect to check for daily reset on component mount/date change
  useEffect(() => {
    if (!hasActiveSubscription()) {
      const today = getTodayString();
      const userKey = currentUser ? currentUser.uid : 'anonymous_user';
      const savedLastViewDate = localStorage.getItem(`lastViewDate_${userKey}`);
      
      // Check if it's a new day and reset if needed
      if (savedLastViewDate && savedLastViewDate !== today) {
        setFlightViews(0);
        setHasViewedFirstFlight(false);
        setLastViewDate(today);
        localStorage.setItem(`flightViews_${userKey}`, '0');
        localStorage.setItem(`hasViewedFirstFlight_${userKey}`, 'false');
        localStorage.setItem(`lastViewDate_${userKey}`, today);
      }
    }
  }, [currentUser, hasActiveSubscription]); // Fixed dependencies

  // Set up interval to check for daily reset every hour
  useEffect(() => {
    if (!hasActiveSubscription()) {
      const checkDailyReset = () => {
        const userKey = currentUser ? currentUser.uid : 'anonymous_user';
        const savedLastViewDate = localStorage.getItem(`lastViewDate_${userKey}`);
        const today = getTodayString();
        
        if (savedLastViewDate && savedLastViewDate !== today) {
          setFlightViews(0);
          setHasViewedFirstFlight(false);
          setLastViewDate(today);
          localStorage.setItem(`flightViews_${userKey}`, '0');
          localStorage.setItem(`hasViewedFirstFlight_${userKey}`, 'false');
          localStorage.setItem(`lastViewDate_${userKey}`, today);
        }
      };

      // Check immediately
      checkDailyReset();
      
      // Set up interval to check every hour
      const interval = setInterval(checkDailyReset, 60 * 60 * 1000); // 1 hour
      
      return () => clearInterval(interval);
    }
  }, [currentUser, hasActiveSubscription]);

  // Save flight views to localStorage
  useEffect(() => {
    const userKey = currentUser ? currentUser.uid : 'anonymous_user';
    localStorage.setItem(`flightViews_${userKey}`, flightViews.toString());
    localStorage.setItem(`hasViewedFirstFlight_${userKey}`, hasViewedFirstFlight.toString());
    if (lastViewDate) {
      localStorage.setItem(`lastViewDate_${userKey}`, lastViewDate);
    }
  }, [currentUser, flightViews, hasViewedFirstFlight, lastViewDate]);

  // Reset views when user gets premium
  useEffect(() => {
    if (!loading && hasActiveSubscription()) {
      setFlightViews(0);
      setHasViewedFirstFlight(false);
      setLastViewDate(null);
      if (currentUser) {
        localStorage.removeItem(`flightViews_${currentUser.uid}`);
        localStorage.removeItem(`hasViewedFirstFlight_${currentUser.uid}`);
        localStorage.removeItem(`lastViewDate_${currentUser.uid}`);
      }
    }
  }, [subscription, loading, currentUser, hasActiveSubscription]);

  const incrementFlightView = async () => {
    if (!hasActiveSubscription()) {
      if (currentUser) {
        // Signed-in user: Update Firebase
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const todayFirebase = getTodayFirebaseString();
          
          // Check if we've already viewed 5 flights today
          if (flightViews >= 5) {
            console.log('⚠️ Already viewed 5 flights today - not incrementing');
            return;
          }
          
          const newViews = flightViews + 1;
          
          // Update Firebase
          await updateDoc(userDocRef, {
            'flightViews.flightCount': newViews,
            'flightViews.lastViewDate': todayFirebase,
            'flightViews.hasViewedFirstFlight': true,
            'flightViews.updatedAt': serverTimestamp()
          });
          
          // Update local state
          setFlightViews(newViews);
          setHasViewedFirstFlight(true);
          setLastViewDate(todayFirebase);
          
          console.log('📊 Flight views updated in Firebase:', { 
            from: flightViews, 
            to: newViews, 
            limit: 5, 
            remaining: Math.max(0, 5 - newViews),
            willShowOverlay: newViews >= 5
          });
          
        } catch (error) {
          console.error('❌ Error updating flight views in Firebase:', error);
          // Fallback to localStorage if Firebase fails
          const userKey = currentUser.uid;
          const today = getTodayString();
          const savedViews = localStorage.getItem(`flightViews_${userKey}`);
          const currentViewCount = savedViews ? parseInt(savedViews, 10) : 0;
          
          if (currentViewCount >= 5) {
            console.log('⚠️ Already viewed 5 flights today - not incrementing');
            return;
          }
          
          const newViews = currentViewCount + 1;
          localStorage.setItem(`flightViews_${userKey}`, newViews.toString());
          localStorage.setItem(`hasViewedFirstFlight_${userKey}`, 'true');
          localStorage.setItem(`lastViewDate_${userKey}`, today);
          
          setFlightViews(newViews);
          setHasViewedFirstFlight(true);
          setLastViewDate(today);
        }
      } else {
        // Anonymous user: Update localStorage
        const userKey = 'anonymous_user';
        const today = getTodayString();
        
        // Check if we've already viewed a flight today
        const savedViews = localStorage.getItem(`flightViews_${userKey}`);
        const currentViewCount = savedViews ? parseInt(savedViews, 10) : 0;
        
        // Prevent double incrementing
        if (currentViewCount >= 5) {
          console.log('⚠️ Already viewed 5 flights today - not incrementing');
          return;
        }
        
        const newViews = currentViewCount + 1;
        
        // Update localStorage
        localStorage.setItem(`flightViews_${userKey}`, newViews.toString());
        localStorage.setItem(`hasViewedFirstFlight_${userKey}`, 'true');
        localStorage.setItem(`lastViewDate_${userKey}`, today);
        
        // Update local state
        setFlightViews(newViews);
        setHasViewedFirstFlight(true);
        setLastViewDate(today);
        
        console.log('📊 Flight views updated in localStorage:', { 
          from: currentViewCount, 
          to: newViews, 
          limit: 5, 
          remaining: Math.max(0, 5 - newViews),
          willShowOverlay: newViews >= 5
        });
      }
    } else {
      console.log('🚫 Premium user - no view increment needed');
    }
  };

  const canViewFlightDetails = () => {
    // Premium users can view unlimited flights
    if (hasActiveSubscription()) {
      console.log('✅ Premium user - unlimited access');
      return true;
    }
    
    // Free users can view 5 flights per day
    const canView = flightViews < 5;
    console.log('🔍 Can view flight details:', {
      flightViews,
      canView,
      userType: currentUser ? 'logged-in' : 'anonymous',
      limit: 5,
      remaining: Math.max(0, 5 - flightViews),
      hasActiveSubscription: hasActiveSubscription(),
      subscription: subscription?.status
    });
    return canView;
  };

  const shouldShowUpgradeOverlay = () => {
    // Don't show overlay for premium users
    if (hasActiveSubscription()) {
      console.log('🚫 Premium user - no upgrade overlay');
      return false;
    }
    
    // Show overlay if user has viewed 5 or more flights today
    const showOverlay = flightViews >= 5;
    console.log('🎭 Should show upgrade overlay:', {
      flightViews,
      showOverlay,
      hasActiveSubscription: hasActiveSubscription(),
      subscription: subscription?.status
    });
    return showOverlay;
  };

  const shouldShowFirstFlightPopup = () => {
    // Always return false - no more first flight popup
    return false;
  };

  const getRemainingDailyViews = () => {
    // Premium users have unlimited views
    if (hasActiveSubscription()) {
      return 'unlimited';
    }
    
    // Free users get 5 views per day
    const remaining = Math.max(0, 5 - flightViews);
    return remaining;
  };

  const getDailyLimitMessage = () => {
    if (hasActiveSubscription()) {
      return 'Unlimited flights with Premium!';
    }
    
    const remaining = getRemainingDailyViews();
    if (remaining === 0) {
      return 'Daily limit reached. Upgrade for unlimited flights!';
    } else if (remaining === 1) {
      return '1 free flight remaining today';
    }
    
    return `${remaining} free flights remaining today`;
  };

  // Debug function to manually reset daily limit (for testing)
  const resetDailyLimit = () => {
    if (!hasActiveSubscription()) {
      const today = getTodayString();
      const userKey = currentUser ? currentUser.uid : 'anonymous_user';
      setFlightViews(0);
      setHasViewedFirstFlight(false);
      setLastViewDate(today);
      localStorage.setItem(`flightViews_${userKey}`, '0');
      localStorage.setItem(`hasViewedFirstFlight_${userKey}`, 'false');
      localStorage.setItem(`lastViewDate_${userKey}`, today);
      console.log('Daily limit reset manually for testing');
    }
  };

  // Debug function to clear all anonymous user data (for testing)
  const clearAnonymousData = () => {
    localStorage.removeItem('flightViews_anonymous_user');
    localStorage.removeItem('hasViewedFirstFlight_anonymous_user');
    localStorage.removeItem('lastViewDate_anonymous_user');
    setFlightViews(0);
    setHasViewedFirstFlight(false);
    setLastViewDate(null);
    console.log('Anonymous user data cleared for testing');
  };

  // Debug function to check current state
  const debugCurrentState = () => {
    const userKey = currentUser ? currentUser.uid : 'anonymous_user';
    const today = getTodayString();
    
    console.log('🐛 DEBUG - Current State:', {
      userKey,
      currentUser: currentUser ? 'logged-in' : 'anonymous',
      flightViews,
      hasViewedFirstFlight,
      lastViewDate,
      today,
      canView: canViewFlightDetails(),
      shouldShowOverlay: shouldShowUpgradeOverlay(),
      localStorage: {
        views: localStorage.getItem(`flightViews_${userKey}`),
        firstView: localStorage.getItem(`hasViewedFirstFlight_${userKey}`),
        lastDate: localStorage.getItem(`lastViewDate_${userKey}`)
      }
    });
  };

  // Test function to simulate flight view
  const testFlightView = () => {
    console.log('🧪 Testing flight view increment...');
    incrementFlightView();
    setTimeout(() => {
      debugCurrentState();
    }, 100);
  };

  // Test function to simulate multiple calls (should only increment once)
  const testMultipleCalls = () => {
    console.log('🧪 Testing multiple calls (should only increment once)...');
    incrementFlightView();
    incrementFlightView();
    incrementFlightView();
    setTimeout(() => {
      debugCurrentState();
    }, 200);
  };

  // Test function to set flight views to 5 (for testing limit)
  const setFlightViewsToLimit = () => {
    const userKey = currentUser ? currentUser.uid : 'anonymous_user';
    
    setFlightViews(5);
    setHasViewedFirstFlight(true);
    localStorage.setItem(`flightViews_${userKey}`, '5');
    localStorage.setItem(`hasViewedFirstFlight_${userKey}`, 'true');
    
    console.log('🧪 Flight views set to 5 for testing limit');
    setTimeout(() => {
      debugCurrentState();
    }, 100);
  };

  // Add global debug functions for easy testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.debugFlightView = {
        debugCurrentState,
        clearAnonymousData,
        resetDailyLimit,
        testFlightView,
        testMultipleCalls,
        setFlightViewsToLimit,
        getCurrentState: () => ({
          flightViews,
          hasViewedFirstFlight,
          lastViewDate,
          canView: canViewFlightDetails(),
          shouldShowOverlay: shouldShowUpgradeOverlay(),
          userType: currentUser ? 'logged-in' : 'anonymous'
        })
      };
    }
  }, [flightViews, hasViewedFirstFlight, lastViewDate, currentUser]);

  const value = React.useMemo(() => ({
    flightViews,
    hasViewedFirstFlight,
    lastViewDate,
    isLoading,
    incrementFlightView,
    canViewFlightDetails,
    shouldShowUpgradeOverlay,
    shouldShowFirstFlightPopup,
    getRemainingDailyViews,
    getDailyLimitMessage,
    resetDailyLimit, // Add debug function to context
    clearAnonymousData, // Add debug function to clear anonymous data
    debugCurrentState // Add debug function to check current state
  }), [
    flightViews,
    hasViewedFirstFlight,
    lastViewDate,
    isLoading,
    currentUser // technically not used in value but used in functions
  ]);

  return (
    <FlightViewContext.Provider value={value}>
      {children}
    </FlightViewContext.Provider>
  );
};